<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid job-info-container">
        <v-card class="p-b-40 p-t-30 p-l-30 p-r-30">
          <div class="el-loading-mask" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Job Name -->
          <div class="row">
            <div class="col-md-6 header">
              <h3 class="title-7 p-b-10">
                <v-btn
                  text
                  icon
                  class="m-0"
                  @click="$router.back()">
                  <i class="zmdi zmdi-long-arrow-left"></i>
                </v-btn>
                {{ job.name }}
              </h3>
            </div>
            <div class="col-md-6 text-right">
              <v-btn
                color="primary"
                class="m-r-10"
                @click.stop="editJob()">
                <i class="zmdi zmdi-edit p-r-10" style="font-size: 1.4rem;"></i> {{ text('edit_btn') }}
              </v-btn>
              <v-btn
                v-if="jobStatus === 'paused'"
                color="success"
                class="m-0 white--text"
                @click="startStream()">
                <i class="fa fa-play p-r-10"></i> {{ text('start_stream_btn') }}
              </v-btn>
              <v-btn
                v-else
                color="error"
                class="m-0 white--text"
                @click="stopStream()">
                <i class="fa fa-pause p-r-10"></i> {{ text('stop_stream_btn') }}
              </v-btn>
            </div>
          </div>
          <v-divider></v-divider>
          <!-- Status and general Info -->
          <div class="row">
            <div class="col-md-2">
              <div class="info-title">
                {{ text('job_status') }}
              </div>
              <div>
                <i class="fa fa-pause job-status--paused"
                   v-if="jobStatus === 'paused'">
                </i>
                <i class="fa fa-circle-notch fa-spin job-status--finished"
                   v-else-if="jobStatus === 'processing_live'">
                </i>
                <i class="fa fa-exclamation status--warning"
                   v-else-if="jobStatus === 'issues'">
                </i>
                <i class="fa fa-exclamation-circle job-status--failed"
                   v-else-if="jobStatus === 'critical'">
                </i>
                <span class="ml-2 status">{{ text(jobStatus) }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-title">
                {{ text('profile') }}
              </div>
              <div class="p-b-5" v-if="!job.profile.name">
                {{ job.profile.name || 'N/A' }}
              </div>
              <v-btn
                v-else
                small
                outline
                class="m-0"
                @click="profileInfoDialog=true">
                {{ job.profile.name }}
              </v-btn>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                {{ text('stream_tester') }}
              </div>
              <div>
                <a href="https://hls-js.netlify.app/demo/" target="_blank">
                  <i class="fa fa-play-circle p-r-5"></i> {{ text('open_btn') }}
                </a>
              </div>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                <i class="zmdi zmdi-calendar p-r-10"></i> {{ text('created') }}
              </div>
              <div>
                {{ formatTime(job.create_time) }}
              </div>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                <i class="zmdi zmdi-calendar p-r-10"></i> {{ text('updated') }}
              </div>
              <div>
                {{ formatTime(job.update_time) }}
              </div>
            </div>
            <div class="col-md-1">
              <div class="info-title">
                <i class="zmdi zmdi-file-text p-r-5"></i>
                ID
              </div>
              <div>
                {{ job.id }}
              </div>
            </div>
          </div>
          <v-divider></v-divider>
          <!-- Input & Output -->
          <div class="row">
            <div class="col-md-12">
              <!-- Input -->
              <table class="job-output m-t-50">
                <tbody>
                <tr>
                  <td class="column-title">{{ text('input_live_header') }}</td>
                  <td>
                    <i class="fa fa-pause job-status--paused"
                       v-if="jobStatus === 'paused' && job.input.state === 'pending'">
                    </i>
                    <i class="fa fa-clock job-status--paused"
                       v-if="jobStatus !== 'paused' && job.input.state === 'pending'">
                    </i>
                    <i class="fa fa-circle-notch fa-spin job-status--finished"
                       v-else-if="job.input.state === 'processing'">
                    </i>
                    <i class="fa fa-exclamation-circle job-status--failed"
                       v-else-if="job.input.state === 'error'">
                    </i>
                    <span class="ml-2 status">{{ text(inputStates[job.input.state]) }}</span>
                  </td>
                </tr>
                <tr v-if="job.input.error">
                  <td class="column-error">{{ text('error') }}</td>
                  <td colspan="2" class="column-error">{{ job.input.error }}</td>
                </tr>
                <tr>
                  <td>URL</td>
                  <td class="link" colspan="2">
                    <a :href="job.input.url" target="_blank">{{ job.input.url }}</a>
                    <i @click="handleCopy(job.input.url)" class="zmdi zmdi-copy m-l-10"></i>
                  </td>
                </tr>
                </tbody>
              </table>
              <!-- Outputs -->
              <table class="job-output m-t-50" v-for="(output, i) of job.outputs" :key="output.id">
                <tbody>
                <tr>
                  <td class="column-title">
                    {{ text('output_stream') }} <span v-if="job.outputs.length > 1">#{{ i+1 }}</span>
                  </td>
                  <td colspan="2">
                    <i class="fa fa-pause job-status--paused"
                       v-if="jobStatus === 'paused' && output.state === 'pending'">
                    </i>
                    <i class="fa fa-clock job-status--paused"
                       v-if="jobStatus !== 'paused' && output.state === 'pending'">
                    </i>
                    <i class="fa fa-circle-notch fa-spin job-status--finished"
                       v-else-if="output.state === 'processing'">
                    </i>
                    <i class="fa fa-exclamation-circle job-status--failed"
                       v-else-if="output.state === 'error'">
                    </i>
                    <span class="ml-2 status">{{ text(outputStates[output.state]) }}</span>
                  </td>
                </tr>
                <tr v-if="output.error">
                  <td class="column-error">{{ text('error') }}</td>
                  <td colspan="2" class="column-error">{{ output.error }}</td>
                </tr>
                <tr v-for="(url, urlIndex) of output.url" :key="url">
                  <td>
                    URL
                    <span v-if="output.url.length > 1">
                      #{{ urlIndex+1 }}
                    </span>
                  </td>
                  <td class="link" colspan="2">
                    <a :href="outputURL(url)" target="_blank">{{ outputURL(url) }}</a>
                    <i @click="handleCopy(outputURL(url))" class="zmdi zmdi-copy m-l-10"></i>
                    <a :href="createExtURL(outputURL(url), output.format)"
                       v-if="['segmented_hls', 'segmented_dash'].includes(output.format)"
                       target="_blank">
                      <i class="fa fa-play-circle m-l-10"></i>
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>{{ text('format') }}</td>
                  <td colspan="2">
                    <span class="blocked-text">
                      {{ formats[output.format] }}
                      <span v-if="output.format === 'segmented_hls'">
                        v{{ output.hls_version || 3 }}
                      </span>
                    </span>
                    <span class="p-l-10" v-if="output.segment_size">
                      {{ text('segment_duration') }}: {{ output.segment_size }} {{ text('sec') }}
                    </span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card>

        <!-- Profile Info Dialog -->
        <v-dialog v-model="profileInfoDialog" max-width="900">
          <v-card class="p-b-15" v-if="!loading">
            <v-card-text>
              <div class="row">
                <div class="col-md-12">
                  <v-btn icon @click="profileInfoDialog = false">
                    <v-icon>zmdi-close</v-icon>
                  </v-btn>
                  <span class="headline align-middle"> {{ job.profile.name }}</span>
                </div>
              </div>
              <v-divider/>
              <table class="profile-detail-table">
                <tbody>
                <tr>
                  <td><span class="card-item-title">ID</span></td>
                  <td>{{ job.profile.id }}</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Audio codec</span></td>
                  <td class="text-uppercase">{{ job.profile.audio_template.codec }}</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Video codec</span></td>
                  <td>
                    <span class="text-uppercase" v-if="job.profile.video_template.codec">
                      {{ videoCodecMap[job.profile.video_template.codec][0] }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Video streams</span></td>
                  <td>{{ job.profile.video_template.streams.length }}</td>
                </tr>
                <tr v-if="job.profile.video_template.codec.startsWith('h26')">
                  <td><span class="card-item-title">Encoder</span></td>
                  <td>
                    <span v-if="job.profile.video_template.codec">
                      {{ videoCodecMap[job.profile.video_template.codec][1]
                      || videoCodecMap[job.profile.video_template.codec][2] }}
                    </span>
                  </td>
                </tr>
                </tbody>
              </table>
              <v-divider/>
              <table class="profile-detail-table">
                <tbody>
                <tr>
                  <td><span class="card-item-title">Audio channels</span></td>
                  <td>
                    {{ audioChannelsMap[job.profile.audio_template.channels] || 'Same as source' }}
                  </td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Audio bitrate</span></td>
                  <td>{{ job.profile.audio_template.bitrate }} Kbps</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Sample rate</span></td>
                  <td>
                    <span v-if="job.profile.audio_template.sample_rate">
                      {{ job.profile.audio_template.sample_rate }} Hz
                    </span>
                    <span v-else>Same as source</span>
                  </td>
                </tr>
                </tbody>
              </table>
              <template v-if="job.profile.video_template.streams.length">
                <table class="profile-streams m-t-15">
                  <thead>
                  <tr>
                    <th class="number-column">Video stream</th>
                    <th v-if="job.profile.video_template.codec.startsWith('h264')">
                      Profile
                    </th>
                    <th v-if="job.profile.video_template.codec.startsWith('h265')">
                      Profile
                    </th>
                    <th v-if="job.profile.video_template.codec.startsWith('h26')">
                      Level
                    </th>
                    <th>Resolution</th>
                    <th>Bitrate, Kbps</th>
                    <th>Resize Mode</th>
                    <th>Frame Rate</th>
                    <th>KeyInt / MinKeyInt</th>
                    <th v-if="job.profile.video_template.codec.startsWith('h26')">
                      Ref Frames / BFrames
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(stream, i) of job.profile.video_template.streams" :key="i">
                    <td>#{{ i+1 }}</td>
                    <td v-if="job.profile.video_template.codec.startsWith('h264')">
                      {{ h264ProfileMap[stream.h264_codec_profile] }}
                    </td>
                    <td v-if="job.profile.video_template.codec.startsWith('h265')">
                      {{ h265ProfileMap[stream.h264_codec_profile] }}
                    </td>
                    <td v-if="job.profile.video_template.codec.startsWith('h264')">
                      {{ stream.h264_codec_level || 'Auto' }}
                    </td>
                    <td v-if="job.profile.video_template.codec.startsWith('h265')">
                      {{ stream.h264_codec_level || 'Auto' }}
                    </td>
                    <td>{{ stream.resolution || 'Same as source'}}</td>
                    <td>{{ stream.bitrate }}</td>
                    <td>{{ resizeModeMap[stream.aspect_ratio] || 'Same as source' }}</td>
                    <td>
                      <span v-if="stream.target_fps">{{ stream.target_fps }} fps</span>
                      <span v-else>Same as source</span>
                    <td>{{ stream.keyframe_int || '0' }}/{{ stream.min_keyframe_int || '0' }}</td>
                    <td v-if="job.profile.video_template.codec.startsWith('h26')">
                      {{ stream.h264_reference_frames || '0' }}/{{ stream.h264_bframes || '0' }}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </template>
            </v-card-text>
          </v-card>
        </v-dialog>
        <!-- -->

        <v-card class="m-t-20 m-b-30 p-b-30 p-t-30 p-l-30 p-r-30" v-if="frameStream">
          <div class="row">
            <div class="col-md-12 header p-l-25">
              <h3 class="title-7 p-b-10">
                {{ text('real_time_metrics' )}}
              </h3>
              <br/>
              {{ frameStream }}
            </div>
            <div class="col-md-12">
              <iframe id="serviceFrameSend"
                      :src="frameSrc"
                      width="100%"
                      height="450"
                      frameborder="0">
              </iframe>
            </div>
          </div>
        </v-card>

        <!-- Meta -->
        <v-card class="m-t-20 m-b-30 p-b-30 p-t-30 p-l-30 p-r-30 grey lighten-4" v-if="showMeta">
           <div class="row">
            <div class="col-md-12 header">
              <h3 class="title-7 p-b-10">
                Transcoder
              </h3>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <table class="job-output">
                <tbody>
                <tr>
                  <td>State</td>
                  <td>{{ transcoder.state }}</td>
                </tr>
                <tr>
                  <td>URL</td>
                  <td class="link" colspan="2">
                    <a :href="transcoder.URL" target="_blank">{{ transcoder.URL }}</a>
                  </td>
                </tr>
                <tr v-if="transcoder.cpuLoad">
                  <td>CPU Load</td>
                  <td class="link" colspan="2">
                    {{ transcoder.cpuLoad }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card>

        <!-- Logs -->
        <v-card class="m-t-20 m-b-30 p-b-30 p-t-30 p-l-30 p-r-30">
          <div class="row">
            <div class="col-md-10 header">
              <h3 class="title-7 p-b-10">
                {{ text('logs') }}
              </h3>
            </div>
            <div class="col-md-2 text-right">
              {{ events.length }} {{ text('events_count') }}
            </div>
          </div>
          <div class="row" v-if="events.length > 0">
            <div class="col-md-12">
              <v-timeline dense clipped>
                <v-timeline-item
                  class="mb-0"
                  :color="logColor(events[0])"
                  small>
                  <v-layout justify-space-between>
                    <v-flex xs7>{{ eventMap[events[0].name].message(events[0]) }}</v-flex>
                    <v-flex xs5 text-xs-right>{{ formatLogDate(events[0].timestamp) }}</v-flex>
                  </v-layout>
                </v-timeline-item>
                <template v-if="events.length > 1">
                  <v-timeline-item
                    class="mb-0"
                    hide-dot>
                    <v-btn
                      outline small
                      class="mb-0"
                      @click="displayLogs = !displayLogs">
                      {{ text('show_btn') }} {{ displayLogs ? text('less') : text('more') }}
                    </v-btn>
                  </v-timeline-item>
                  <template v-if="displayLogs">
                    <v-timeline-item
                      v-for="(event, eventIndex) in events.slice(1)"
                      :key="event.timestamp+'i'+eventIndex"
                      class="mb-0"
                      :color="logColor(event)"
                      small>
                      <v-layout justify-space-between>
                        <v-flex xs7>{{ eventMap[event.name].message(event) }}</v-flex>
                        <v-flex xs5 text-xs-right>{{ formatLogDate(event.timestamp) }}</v-flex>
                      </v-layout>
                    </v-timeline-item>
                  </template>
                </template>
              </v-timeline>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { setClipboard } from '@/helpers/utils';
import {
  INPUT_STATES, OUTPUT_STATES, JOB_TYPES, JOB_EVENTS, VIDEO_CODEC_MAP, AUDIO_CHANNELS_MAP,
  H264_CODEC_PROFILE_MAP, H265_CODEC_PROFILE_MAP, RESIZE_MODE_MAP,
} from '@/components/transcoding/shared/types';

function isPaused(job) {
  return job.state === 'ready';
}

function isCriticalTask(job) {
  if (job.input.state === 'error') {
    return true;
  }
  let failed = true;
  for (let i = 0; i < job.outputs.length; i++) {
    const output = job.outputs[i];
    if (output.state !== 'error') {
      failed = false;
      break;
    }
  }
  return failed;
}

function isIssuedTask(job) {
  if (job.input.state === 'error') {
    return false;
  }
  let issued = false;
  for (let i = 0; i < job.outputs.length; i++) {
    const output = job.outputs[i];
    if (output.state === 'error') {
      issued = true;
      break;
    }
  }
  return issued;
}

export default {
  created() {
    const jobId = this.$route.params.id;
    this.$store.commit('setPageLoader');
    this.getJobById(jobId);
    this.pollData();
    if (this.$route.path.includes(`${jobId}/meta`)) {
      this.showMeta = true;
      this.getMeta(jobId);
    }
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  data() {
    return {
      polling: null,
      profileInfoDialog: false,
      job: {
        name: '',
        input: {
          url: '',
        },
        profile: {
          audio_template: { name: '' },
          video_template: { name: '' },
        },
        outputs: [],
      },
      showMeta: false,
      transcoder: {
        extId: '',
        URL: '',
        cpuLoad: 0,
        state: '',
      },
      frameSrc: '',
      frameStream: '',
      logsLoading: false,
      events: [],
      displayLogs: false,
      inputStates: INPUT_STATES,
      outputStates: OUTPUT_STATES,
      formats: JOB_TYPES,
      eventMap: JOB_EVENTS,
      videoCodecMap: VIDEO_CODEC_MAP,
      audioChannelsMap: AUDIO_CHANNELS_MAP,
      h264ProfileMap: H264_CODEC_PROFILE_MAP,
      h265ProfileMap: H265_CODEC_PROFILE_MAP,
      resizeModeMap: RESIZE_MODE_MAP,
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    jobStatus() {
      if (isPaused(this.job)) {
        return 'paused';
      }
      if (isCriticalTask(this.job)) {
        return 'critical';
      }
      if (isIssuedTask(this.job)) {
        return 'issues';
      }
      return 'processing_live';
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    pollData() {
      this.polling = setInterval(() => {
        this.getJobById(this.job.id, false);
      }, 15000);
    },
    getJobById(jobId, notification = true) {
      this.logsLoading = true;
      this.$store.dispatch('jobs/getJobById', jobId)
        .then((job) => {
          const outputMap = {};
          this.job = job;
          for (let i = 0; i < job.outputs.length; i++) {
            outputMap[this.job.outputs[i].id] = i + 1;

            if (this.frameSrc === '' && this.job.outputs[i].format === 'segmented_hls' && this.job.outputs[i].state === 'processing') {
              if (this.job.outputs[i].url.length) {
                const link = this.outputURL(this.job.outputs[i].url[0]);
                this.frameStream = link.replace(/^http:\/\//, 'https://');
                this.frameSrc = `/js/hlsjs/index.html?src=${this.frameStream}`;
              }
            }
          }
          const data = {
            jobId: this.job.id,
            limit: 200,
          };
          this.$store.dispatch('jobs/getLogs', data)
            .then((events) => {
              this.events = [];
              for (let i = 0; i < events.length; i++) {
                const event = events[i];
                if (event.data.output) {
                  event.data.output.index = outputMap[event.data.output.id];
                }
                this.events.push(event);
              }
            })
            // eslint-disable-next-line no-return-assign
            .finally(() => this.logsLoading = false);
        })
        .catch((errorMsg) => {
          if (notification) {
            this.$toasted.error(errorMsg, {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 10000,
            });
          }
          this.$router.push({ name: 'LiveTranscodingDashboard' });
        })
        .finally(() => this.$store.commit('resolvePageLoader'));
    },
    getMeta(jobId) {
      this.$store.dispatch('jobs/getLiveJobMeta', jobId)
        .then((meta) => {
          if (meta.instance && meta.instance.Transcoder) {
            this.transcoder.extId = meta.instance.Transcoder.ExtId;
            this.transcoder.URL = meta.instance.Transcoder.URL;
            this.transcoder.cpuLoad = meta.instance.Transcoder.CPULoad;
            this.transcoder.state = meta.instance.Transcoder.State;
          }
        })
        .catch((errorMsg) => {
          console.log(errorMsg);
        });
    },
    startStream() {
      this.$store.commit('setPageLoader');
      this.$store.dispatch('jobs/startJob', this.job.id)
        .then(() => {
          this.getJobById(this.job.id);
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 10000,
          });
          this.$router.push({ name: 'LiveTranscodingDashboard' });
        });
    },
    stopStream() {
      this.$store.commit('setPageLoader');
      this.$store.dispatch('jobs/stopJob', this.job.id)
        .then(() => {
          this.getJobById(this.job.id);
        })
        .catch((errorMsg) => {
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 10000,
          });
          this.$router.push({ name: 'LiveTranscodingDashboard' });
        });
    },
    formatTime(time) {
      return moment(time).format('DD/MM/YYYY HH:mm');
    },
    handleCopy(text) {
      setClipboard(text);
      this.$toasted.info(this.text('copied'), {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 1000,
      });
    },
    formatLogDate(timestamp) {
      return moment.unix(timestamp).format('DD/MM/YYYY HH:mm');
    },
    logColor(event) {
      if (['job.transc.failed', 'job.output.failed'].includes(event.name)) {
        return '#C71C22';
      }
      if (['job.transc.finished', 'job.output.finished'].includes(event.name)) {
        return '#008d4b';
      }
      return 'grey';
    },
    editJob() {
      this.$router.push({
        name: 'LiveTranscodingJobEditPage',
        params: {
          id: this.job.id,
        },
      });
    },
    outputURL(url) {
      if (url.startsWith('origin')) {
        return url.replace('origin', 'http');
      }
      return url;
    },
    createExtURL(url, format) {
      const link = url.replace(/^http:\/\//, 'https://');
      if (format === 'segmented_dash') {
        return `https://shaka-player-demo.appspot.com/demo/#asset=${link}`;
      }
      return `https://hls-js.netlify.com/demo/?src=${escape(link)}`;
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
  watch: {
    '$route.params.id': function (newId, oldId) {
      if (newId !== oldId) {
        this.getJobById(newId);
      }
    },
  },
};
</script>
